/**
 * vocab.ts — closed vocabularies for the Cuentas a Pagar module. Text columns
 * at the DB, never pgEnum (repo-wide convention). Every one of these is the
 * literal table from the design/spec — no invented eighth value, no
 * shortened action.
 *
 * Kept in its own leaf module (no imports from this package) so both
 * `index.ts` and sibling files (`comprobante-key.ts`,
 * `comprobante-lote-parser.ts`) can import these without a circular
 * module dependency through the barrel.
 *
 * Spec: openspec/changes/add-cuentas-pagar/specs/cuentas-pagar-ledger/spec.md
 * Design: openspec/changes/add-cuentas-pagar/design.md
 *   "packages/contracts-cuentas-pagar/src/" section
 */
import { z } from 'zod';

export const COMPROBANTE_TIPOS = ['FAC', 'NC', 'ND'] as const;
export const ComprobanteTipoSchema = z.enum(COMPROBANTE_TIPOS);
export type ComprobanteTipo = z.infer<typeof ComprobanteTipoSchema>;

export const LLAVE_FUENTES = ['n_comp', 'natural', 'origen_sistema'] as const;
export const LlaveFuenteSchema = z.enum(LLAVE_FUENTES);
export type LlaveFuente = z.infer<typeof LlaveFuenteSchema>;

export const COMPROBANTE_ORIGENES = ['manual', 'importado'] as const;
export const ComprobanteOrigenSchema = z.enum(COMPROBANTE_ORIGENES);
export type ComprobanteOrigen = z.infer<typeof ComprobanteOrigenSchema>;

export const VENCIMIENTO_FUENTES = [
  'informado',
  'condicion_oc',
  'condicion_proveedor',
  'plazo_default_tenant',
] as const;
export const VencimientoFuenteSchema = z.enum(VENCIMIENTO_FUENTES);
export type VencimientoFuente = z.infer<typeof VencimientoFuenteSchema>;

/** `certificacion` (2026-10-27): a signed certification attached to the invoice (`adjuntoFileId`), no linked record. */
export const RESPALDO_TIPOS = ['orden_compra', 'contrato', 'certificacion_locx', 'certificacion', 'ninguno'] as const;
export const RespaldoTipoSchema = z.enum(RESPALDO_TIPOS);
export type RespaldoTipo = z.infer<typeof RespaldoTipoSchema>;

/** Extension point — only value resolved today is 'centro_costo' (catalog.cost_center). */
export const DESTINO_COSTO_TIPOS = ['centro_costo'] as const;
export const DestinoCostoTipoSchema = z.enum(DESTINO_COSTO_TIPOS);
export type DestinoCostoTipo = z.infer<typeof DestinoCostoTipoSchema>;

export const LEDGER_MOVEMENT_TYPES = [
  'emision_fa',
  'emision_nc',
  'emision_nd',
  'pago',
  // retenciones-en-el-pago AP-2.1 — withholding booked by an Orden de Pago (sign -1, like a pago).
  'retencion',
  'anulacion',
  'saldo_inicial',
] as const;
export const LedgerMovementTypeSchema = z.enum(LEDGER_MOVEMENT_TYPES);
export type LedgerMovementType = z.infer<typeof LedgerMovementTypeSchema>;

/**
 * Perceptions charged on an invoice (2026-10-27, user: "Percepción IIBB, Percepcion IVA, Otros. Se pone el monto. Antes
 * del total."). They add to the total; each one is a column of `sumi.comprobante` (migration 0041).
 */
export const PERCEPCION_TIPOS = ['iibb', 'iva', 'otros'] as const;
export const PercepcionTipoSchema = z.enum(PERCEPCION_TIPOS);
export type PercepcionTipo = z.infer<typeof PercepcionTipoSchema>;
export const PERCEPCION_LABELS: Readonly<Record<PercepcionTipo, string>> = {
  iibb: 'Percepción IIBB',
  iva: 'Percepción IVA',
  otros: 'Otros',
};

/**
 * Ganancias concept of an invoice (migration 0044, 2026-10-28, user: "el caso mas comun es un service de vehiculo, tiene
 * la venta de un bien de cambio, y a la vez es un servicio"). `mixto` carries the bienes de cambio part of the neto.
 */
export const GANANCIAS_CONCEPTOS = ['servicios', 'bienes', 'mixto'] as const;
export const GananciasConceptoSchema = z.enum(GANANCIAS_CONCEPTOS);
export type GananciasConcepto = z.infer<typeof GananciasConceptoSchema>;
export const GANANCIAS_CONCEPTO_LABELS: Readonly<Record<GananciasConcepto, string>> = {
  servicios: 'Servicios',
  bienes: 'Bienes de cambio',
  mixto: 'Mixta (bienes y servicios)',
};

/**
 * Medio de pago of a `pago` movement. CLOSED on the write path only: the DB
 * column stays free `text` because historical rows hold free text
 * ("Transferencia BPN 360638/1"), so every read schema keeps `string | null`
 * and the UI shows the raw value when it is not in this vocabulary
 * (`medioPagoLabel`).
 */
export const MEDIOS_PAGO = [
  'transferencia',
  'cheque',
  'echeq',
  'efectivo',
  'debito_automatico',
  'compensacion',
  'otro',
] as const;
export const MedioPagoSchema = z.enum(MEDIOS_PAGO);
export type MedioPago = z.infer<typeof MedioPagoSchema>;

/** `AAAA-MM-DD` calendar date, shared by the payment requests. */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha debe tener el formato AAAA-MM-DD.');

export const MEDIO_PAGO_LABELS: Readonly<Record<MedioPago, string>> = {
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  echeq: 'eCheq',
  efectivo: 'Efectivo',
  debito_automatico: 'Débito automático',
  compensacion: 'Compensación',
  otro: 'Otro',
};

/** Display label; a historical free-text value (not in the vocabulary) is shown as-is. */
export function medioPagoLabel(value: string | null): string | null {
  if (value === null) return null;
  return (MEDIO_PAGO_LABELS as Readonly<Record<string, string>>)[value] ?? value;
}

/**
 * AP-8.2 — closed calculation modes of a retention type. Config controls the
 * SHAPE (which parameters apply); code controls the BEHAVIOR (`sugerirRetencion`).
 * No formula engine, no logic in configuration.
 */
export const MODOS_CALCULO_RETENCION = ['monto_fijo', 'porcentaje', 'porcentaje_excedente', 'ganancias_mensual', 'manual'] as const;
export const ModoCalculoRetencionSchema = z.enum(MODOS_CALCULO_RETENCION);
export type ModoCalculoRetencion = z.infer<typeof ModoCalculoRetencionSchema>;

/** Which comprobante amount a percentage applies to. */
export const BASES_CALCULO_RETENCION = ['neto', 'total'] as const;
export const BaseCalculoRetencionSchema = z.enum(BASES_CALCULO_RETENCION);
export type BaseCalculoRetencion = z.infer<typeof BaseCalculoRetencionSchema>;

export const MODO_CALCULO_RETENCION_LABELS: Readonly<Record<ModoCalculoRetencion, string>> = {
  monto_fijo: 'Monto fijo',
  porcentaje: 'Porcentaje',
  porcentaje_excedente: 'Porcentaje sobre el excedente',
  ganancias_mensual: 'Ganancias del mes',
  manual: 'Manual',
};

export const BASE_CALCULO_RETENCION_LABELS: Readonly<Record<BaseCalculoRetencion, string>> = {
  neto: 'Neto',
  total: 'Total',
};
